// No-login persistence for a guest's reservation request.
//
// The reserve page (app/reserve/[slug]) submits a request and then polls its
// status by request id , but that id lived only in React state, so a refresh or
// a return visit lost it and the guest could never see whether their table was
// confirmed. This mirrors lib/customer/guest-orders: a small, best-effort
// localStorage record keyed by the request id (which is itself the bearer
// secret for the public status lookup).

export type GuestReservationRecord = {
  /** reservation_requests.id , also the bearer secret for status lookup. */
  id: string;
  /** Menu slug the request was made against (the reserve page handle). */
  slug: string;
  /** ISO datetime requested. */
  whenISO: string;
  partySize: number;
  businessName?: string | null;
  branchName?: string | null;
  createdAt: number;
};

const STORAGE_KEY = 'mnrq:guest-reservations:v1';
const MAX_RECORDS = 10;
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function safeParse(raw: string | null): GuestReservationRecord[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is GuestReservationRecord =>
        Boolean(e) && typeof e.id === 'string' && typeof e.slug === 'string' && typeof e.createdAt === 'number',
    );
  } catch {
    return [];
  }
}

function read(): GuestReservationRecord[] {
  if (typeof window === 'undefined') return [];
  try {
    const cutoff = Date.now() - TTL_MS;
    const fresh = safeParse(window.localStorage.getItem(STORAGE_KEY)).filter((r) => r.createdAt >= cutoff);
    fresh.sort((a, b) => b.createdAt - a.createdAt);
    return fresh;
  } catch {
    return [];
  }
}

export function saveGuestReservation(record: GuestReservationRecord): void {
  if (typeof window === 'undefined' || !record.id) return;
  try {
    const existing = read().filter((r) => r.id !== record.id);
    const next = [{ ...record, createdAt: record.createdAt || Date.now() }, ...existing].slice(0, MAX_RECORDS);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* best-effort */
  }
}

/** The most recent stored request for a given reserve-page slug, if any. */
export function getReservationForSlug(slug: string): GuestReservationRecord | null {
  return read().find((r) => r.slug === slug) || null;
}

/** All stored requests for a given reserve-page slug, newest first. */
export function getReservationsForSlug(slug: string): GuestReservationRecord[] {
  return read().filter((r) => r.slug === slug);
}

/** All stored requests on this device, newest first. Used by the signup claim
 *  step to attach every request to the freshly created account. */
export function listGuestReservations(): GuestReservationRecord[] {
  return read();
}

export function removeGuestReservation(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    const next = read().filter((r) => r.id !== id);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* best-effort */
  }
}
